import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { bumpAuthSessionAtom, currentUserAtom } from "@/atoms/auth";
import { cn } from "@/lib/cn";
import { getBreadcrumbs, navigation } from "@/routes/menuConfig";
import { clearToken } from "@/utils/storage";

import { SidebarFooter, SidebarLogo, SidebarNav } from "./Sidebar";
import { TopHeader } from "./TopHeader";

/** 后台主布局：Coze 侧栏 + 顶栏，菜单后续可换为接口动态下发 */
export const MainLayout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAtomValue(currentUserAtom);
  const bumpAuthSession = useSetAtom(bumpAuthSessionAtom);
  // 侧栏折叠态
  const [collapsed, setCollapsed] = useState(false);
  // 展开的父级菜单名
  const [expandedMenus, setExpandedMenus] = useState<string[]>(["账号管理"]);
  const breadcrumbs = getBreadcrumbs(pathname, navigation);
  const displayName = user?.nickname || user?.username || "管理员";
  const avatarText = (displayName.charAt(0) || "A").toUpperCase();

  /** 切换父菜单展开 */
  const toggleExpand = (name: string) => {
    setExpandedMenus((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  /** 退出登录 */
  const handleLogout = () => {
    clearToken();
    bumpAuthSession();
    navigate("/login", { replace: true });
  };

  return (
    <div className="h-screen flex overflow-hidden bg-slate-50/50">
      <aside
        className={cn(
          "h-screen flex flex-col transition-all duration-300 shrink-0 relative z-20",
          "bg-white shadow-[1px_0_8px_-2px_rgba(0,0,0,0.06)]",
          collapsed ? "w-[72px]" : "w-[272px]",
        )}
      >
        <SidebarLogo collapsed={collapsed} onToggleCollapse={() => setCollapsed((v) => !v)} />
        <SidebarNav
          menus={navigation}
          pathname={pathname}
          collapsed={collapsed}
          expandedMenus={expandedMenus}
          onToggleExpand={toggleExpand}
        />
        <SidebarFooter collapsed={collapsed} onLogout={() => void handleLogout()} />
      </aside>
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopHeader
          collapsed={collapsed}
          breadcrumbs={breadcrumbs}
          displayName={displayName}
          roleName="超级管理员"
          avatarText={avatarText}
          onToggleCollapse={() => setCollapsed((v) => !v)}
          onLogout={() => void handleLogout()}
        />
        <main className="flex-1 p-6 overflow-y-auto overflow-x-hidden bg-slate-50/50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
